/* Ulif Creator Styles v4: Liquid gates approved/published manifest rows and exact HTTPS Instagram sources before this code receives records. */
(function registerUlifCreatorStylesv4(global) {
  'use strict';

  const ELEMENT_NAME = 'ulif-creator-styles-v4';
  const HISTORY_KEY = 'ulifCreatorStylev4';
  const URL_PARAMETER = 'ugc';
  const SECTION_LOAD_FLAG = '__ulifCreatorStylesv4SectionLoadBound';

  function prepareCreatorRecords(input) {
    if (!Array.isArray(input)) return { records: [], duplicateAssetIds: [] };

    const assetIdCounts = new Map();
    const recordKeyCounts = new Map();
    const records = input.reduce((result, record, sourceIndex) => {
      if (!record?.id) return result;
      const id = String(record.id).trim();
      if (!id) return result;

      const baseKey = String(record.recordKey || `${id}:${sourceIndex}`).trim();
      const keyOccurrence = recordKeyCounts.get(baseKey) || 0;
      recordKeyCounts.set(baseKey, keyOccurrence + 1);
      const recordKey = keyOccurrence === 0 ? baseKey : `${baseKey}:${sourceIndex}`;

      assetIdCounts.set(id, (assetIdCounts.get(id) || 0) + 1);
      result.push({ ...record, id, recordKey });
      return result;
    }, []);

    const duplicateAssetIds = [...assetIdCounts.entries()]
      .filter(([, count]) => count > 1)
      .map(([id]) => id)
      .sort();

    return { records, duplicateAssetIds };
  }

  function findCreatorIndexByKey(records, recordKey) {
    return records.findIndex((record) => record.recordKey === recordKey);
  }

  function findUniqueCreatorIndexById(records, id) {
    let match = -1;
    for (let index = 0; index < records.length; index += 1) {
      if (records[index].id !== id) continue;
      if (match >= 0) return -1;
      match = index;
    }
    return match;
  }

  function nextCreatorIndex(currentIndex, recordCount, direction) {
    if (recordCount <= 0) return -1;
    const step = direction < 0 ? -1 : 1;
    if (!Number.isInteger(currentIndex) || currentIndex < 0 || currentIndex >= recordCount) {
      return step < 0 ? recordCount - 1 : 0;
    }
    return (currentIndex + step + recordCount) % recordCount;
  }

  function nextProductIndex(currentIndex, productCount, direction) {
    return nextCreatorIndex(currentIndex, productCount, direction);
  }

  function getResponsiveLimit(isMobile, desktopLimit, mobileLimit) {
    return isMobile ? mobileLimit : desktopLimit;
  }

  function nextVisibleLimit(currentLimit, total, step) {
    return Math.min(Math.max(0, total), Math.max(0, currentLimit) + Math.max(1, step));
  }

  const testApi = {
    findCreatorIndexByKey,
    findUniqueCreatorIndexById,
    getResponsiveLimit,
    nextCreatorIndex,
    nextProductIndex,
    nextVisibleLimit,
    prepareCreatorRecords,
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = testApi;
  global.UlifCreatorStylesV4TestApi = testApi;

  if (!global.HTMLElement || !global.customElements || !global.document) return;

  class UlifCreatorStylesv4 extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.records = [];
      this.activeIndex = -1;
      this.activeProductIndex = -1;
      this.activeProductHandle = null;
      this.duplicateAssetIds = new Set();
      this.lastTrigger = null;
      this.dialog = null;
      this.mediaBackdrop = null;
      this.dialogImage = null;
      this.videoHost = null;
      this.dialogVideo = null;
      this.productRegion = null;
      this.productHost = null;
      this.productControls = null;
      this.productCounter = null;
      this.loadMoreButton = null;
      this.loadMoreLabel = null;
      this.loadMoreSpinner = null;
      this.loadMoreArrow = null;
      this.desktopVisibleLimit = 8;
      this.mobileVisibleLimit = 4;
      this.mobileQuery = null;
      this.isLoadingMore = false;
      this.editorWarning = null;
      this.pendingCloseOptions = null;
    }

    connectedCallback() {
      this.reinitialize();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.clearVideo();
      this.clearProduct();
    }

    reinitialize() {
      this.clearVideo();
      this.clearProduct();
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      const recordModel = this.readRecords();
      this.records = recordModel.records;
      this.duplicateAssetIds = new Set(recordModel.duplicateAssetIds);
      this.dialog = this.querySelector('[data-ugc-dialog]');
      this.mediaBackdrop = this.querySelector('[data-ugc-dialog-backdrop]');
      this.dialogImage = this.querySelector('[data-ugc-dialog-image]');
      this.videoHost = this.querySelector('[data-ugc-dialog-video-host]');
      this.dialogVideo = null;
      this.productRegion = this.querySelector('[data-ugc-dialog-product-region]');
      this.productHost = this.querySelector('[data-ugc-dialog-product-host]');
      this.productControls = this.querySelector('[data-ugc-product-controls]');
      this.productCounter = this.querySelector('[data-ugc-product-counter]');
      this.loadMoreButton = this.querySelector('[data-ugc-load-more]');
      this.loadMoreLabel = this.querySelector('[data-ugc-load-more-label]');
      this.loadMoreSpinner = this.querySelector('[data-ugc-load-more-spinner]');
      this.loadMoreArrow = this.querySelector('[data-ugc-load-more-arrow]');
      this.editorWarning = this.querySelector('[data-ugc-editor-warning]');
      this.desktopVisibleLimit = Math.max(1, Number(this.dataset.initialDesktop) || 8);
      this.mobileVisibleLimit = Math.max(1, Number(this.dataset.initialMobile) || 4);
      this.mobileQuery = typeof global.matchMedia === 'function'
        ? global.matchMedia('(max-width: 749px)')
        : { matches: false };

      this.addEventListener('click', (event) => this.handleClick(event), { signal });
      this.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
      global.addEventListener('popstate', () => this.syncFromUrl(), { signal });
      if (typeof this.mobileQuery.addEventListener === 'function') {
        this.mobileQuery.addEventListener('change', () => this.applyCardVisibility(), { signal });
      }

      if (this.dialog) {
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.closeDialog({ updateUrl: true, restoreFocus: true });
        }, { signal });
        this.dialog.addEventListener('close', () => {
          const options = this.pendingCloseOptions || { restoreFocus: true };
          this.pendingCloseOptions = null;
          if (this.activeIndex >= 0) this.completeClose(options);
        }, { signal });
      }

      this.renderRecordWarnings();
      this.applyCardVisibility();
      this.syncFromUrl();
    }

    readRecords() {
      const source = this.querySelector('[data-ugc-records]');
      if (!source?.textContent) return prepareCreatorRecords([]);
      try {
        const parsed = JSON.parse(source.textContent);
        return prepareCreatorRecords(parsed);
      } catch (error) {
        console.warn('Ulif Creator Styles v4 could not read UGC records.', error);
        return prepareCreatorRecords([]);
      }
    }

    renderRecordWarnings() {
      const duplicateIds = [...this.duplicateAssetIds];
      if (duplicateIds.length) {
        console.warn(`Ulif Creator Styles v4 requires unique asset IDs. Duplicate values: ${duplicateIds.join(', ')}`);
      }
      if (!this.editorWarning) return;
      this.editorWarning.hidden = duplicateIds.length === 0;
      this.editorWarning.textContent = duplicateIds.length
        ? `Duplicate Approved manifest asset ID: ${duplicateIds.join(', ')}. Give every Creator style a unique manifest asset ID before publishing.`
        : '';
    }

    handleClick(event) {
      const close = event.target.closest('[data-ugc-dialog-close]');
      if (close && this.contains(close)) return this.closeDialog({ updateUrl: true, restoreFocus: true });
      const previous = event.target.closest('[data-ugc-dialog-prev]');
      if (previous && this.contains(previous)) return this.moveDialog(-1);
      const next = event.target.closest('[data-ugc-dialog-next]');
      if (next && this.contains(next)) return this.moveDialog(1);
      const previousProduct = event.target.closest('[data-ugc-product-prev]');
      if (previousProduct && this.contains(previousProduct)) return this.moveProduct(-1);
      const nextProduct = event.target.closest('[data-ugc-product-next]');
      if (nextProduct && this.contains(nextProduct)) return this.moveProduct(1);
      const product = event.target.closest('[data-ugc-dialog-product-host] a[href]');
      if (product && this.contains(product)) {
        this.emit('ulif:ugc_product_click', this.getActiveRecord(), {
          productHandle: this.activeProductHandle,
          productIndex: this.activeProductIndex,
          productUrl: product.href,
        });
      }
      const source = event.target.closest('[data-ugc-dialog-source]');
      if (source && this.contains(source)) this.emit('ulif:ugc_source_click', this.getActiveRecord());
      const follow = event.target.closest('[data-ugc-follow-link]');
      if (follow && this.contains(follow)) this.emit('ulif:ugc_follow_click', null);
      const loadMore = event.target.closest('[data-ugc-load-more]');
      if (loadMore && this.contains(loadMore)) return this.revealMoreCards();
      if (this.dialog && event.target === this.dialog) return this.closeDialog({ updateUrl: true, restoreFocus: true });
      const card = event.target.closest('[data-ugc-card][data-ugc-key]');
      if (card && this.contains(card)) this.openByKey(card.dataset.ugcKey, { historyMode: 'push', trigger: card, restoreFocus: true });
    }

    handleKeydown(event) {
      if (!this.isDialogOpen()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDialog({ updateUrl: true, restoreFocus: true });
      }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.moveDialog(-1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); this.moveDialog(1); }
    }

    openByKey(recordKey, options = {}) {
      const index = findCreatorIndexByKey(this.records, recordKey);
      this.openByIndex(index, options);
    }

    openByIndex(index, options = {}) {
      const record = this.records[index];
      if (!record || !this.dialog) return;
      const { historyMode = 'none', trigger = null, restoreFocus = false } = options;
      const changed = this.activeIndex !== index;
      const card = trigger || this.getCard(record.recordKey);
      if (card?.hidden) this.revealCard(card);
      if (restoreFocus && card) this.lastTrigger = card;

      this.activeIndex = index;
      this.setActiveCard(record.recordKey);
      this.renderRecord(record, index);
      if (!this.isDialogOpen()) {
        if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
      }
      if (historyMode === 'push') this.writeUrl(record, 'push');
      if (historyMode === 'replace') this.writeUrl(record, 'replace');
      if (changed) this.emit('ulif:ugc_open', record);
      this.dialog.querySelector('[data-ugc-dialog-close]')?.focus({ preventScroll: true });
    }

    renderRecord(record, index) {
      const setText = (selector, value, hideWhenBlank = false) => {
        const element = this.dialog?.querySelector(selector);
        if (!element) return;
        element.textContent = value || '';
        if (hideWhenBlank) element.hidden = !value;
      };
      setText('[data-ugc-dialog-title]', record.title || `@${record.creatorHandle}`);
      setText('[data-ugc-dialog-handle]', record.creatorName ? `${record.creatorName} | @${record.creatorHandle}` : `@${record.creatorHandle}`);
      setText('[data-ugc-dialog-caption]', record.caption, true);
      setText('[data-ugc-dialog-disclosure]', record.disclosure, true);
      setText('[data-ugc-dialog-counter]', index >= 0 ? `${index + 1} / ${this.records.length}` : '');

      this.renderProduct(record, 0);
      const source = this.dialog?.querySelector('[data-ugc-dialog-source]');
      if (source) {
        source.hidden = !record.sourceUrl;
        if (record.sourceUrl) source.href = record.sourceUrl;
        else source.removeAttribute('href');
      }

      if (this.mediaBackdrop) {
        if (record.posterSrc) this.mediaBackdrop.src = record.posterSrc;
        else this.mediaBackdrop.removeAttribute('src');
      }
      if (this.dialogImage) {
        this.dialogImage.src = record.posterSrc;
        this.dialogImage.alt = record.alt || '';
        this.dialogImage.hidden = Boolean(record.videoSrc);
      }
      this.clearVideo();
      if (record.videoSrc) this.createVideo(record);
    }

    closeDialog(options = {}) {
      const { updateUrl = true, restoreFocus = true } = options;
      if (!this.isDialogOpen()) return;
      if (updateUrl) this.clearUrl();
      this.clearVideo();
      this.clearProduct();
      if (typeof this.dialog.close === 'function') {
        this.pendingCloseOptions = { restoreFocus };
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.completeClose({ restoreFocus });
      }
    }

    completeClose({ restoreFocus = true } = {}) {
      const record = this.getActiveRecord();
      this.clearVideo();
      this.clearProduct();
      if (record) this.emit('ulif:ugc_close', record);
      this.activeIndex = -1;
      this.querySelectorAll('[data-ugc-card]').forEach((card) => {
        card.removeAttribute('data-ugc-active');
        card.removeAttribute('aria-current');
      });
      if (restoreFocus && this.lastTrigger?.isConnected) this.lastTrigger.focus({ preventScroll: true });
      this.lastTrigger = null;
    }

    moveDialog(direction) {
      if (this.activeIndex < 0 || !this.records.length) return;
      const nextIndex = nextCreatorIndex(this.activeIndex, this.records.length, direction);
      this.openByIndex(nextIndex, { historyMode: 'replace', restoreFocus: false });
    }

    getActiveRecord() { return this.records[this.activeIndex] || null; }

    getCard(recordKey) {
      return [...this.querySelectorAll('[data-ugc-card][data-ugc-key]')].find((card) => card.dataset.ugcKey === recordKey) || null;
    }

    setActiveCard(recordKey) {
      this.querySelectorAll('[data-ugc-card]').forEach((card) => {
        const active = card.dataset.ugcKey === recordKey;
        card.toggleAttribute('data-ugc-active', active);
        if (active) card.setAttribute('aria-current', 'true');
        else card.removeAttribute('aria-current');
      });
    }

    getCards() {
      return [...this.querySelectorAll('[data-ugc-card][data-ugc-index]')];
    }

    isMobileLayout() {
      return Boolean(this.mobileQuery?.matches);
    }

    getCurrentVisibleLimit() {
      return getResponsiveLimit(this.isMobileLayout(), this.desktopVisibleLimit, this.mobileVisibleLimit);
    }

    getCurrentLoadStep() {
      const desktopStep = Math.max(1, Number(this.dataset.loadStepDesktop) || 8);
      const mobileStep = Math.max(1, Number(this.dataset.loadStepMobile) || 4);
      return getResponsiveLimit(this.isMobileLayout(), desktopStep, mobileStep);
    }

    setCurrentVisibleLimit(limit) {
      if (this.isMobileLayout()) this.mobileVisibleLimit = limit;
      else this.desktopVisibleLimit = limit;
    }

    applyCardVisibility() {
      const visibleLimit = this.getCurrentVisibleLimit();
      this.getCards().forEach((card, fallbackIndex) => {
        const parsedIndex = Number(card.dataset.ugcIndex);
        const index = Number.isFinite(parsedIndex) ? parsedIndex : fallbackIndex;
        const hidden = index >= visibleLimit;
        card.hidden = hidden;
        card.toggleAttribute('data-ugc-hidden', hidden);
        card.removeAttribute('data-ugc-mobile-initial-hidden');
      });
      this.updateLoadMoreControl();
    }

    async revealMoreCards() {
      if (this.isLoadingMore) return;
      const cards = this.getCards();
      const hiddenBefore = new Set(cards.filter((card) => card.hidden));
      if (!hiddenBefore.size) return this.updateLoadMoreControl();

      this.setLoadMoreState(true);
      let revealedCards = [];
      try {
        const reduceMotion = typeof global.matchMedia === 'function'
          && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
        await new Promise((resolve) => global.setTimeout(resolve, reduceMotion ? 0 : 220));
        const nextLimit = nextVisibleLimit(this.getCurrentVisibleLimit(), cards.length, this.getCurrentLoadStep());
        this.setCurrentVisibleLimit(nextLimit);
        this.applyCardVisibility();
        revealedCards = cards.filter((card) => hiddenBefore.has(card) && !card.hidden);
        if (!reduceMotion) {
          revealedCards.forEach((card) => {
            card.setAttribute('data-ugc-revealing', '');
            global.setTimeout(() => card.removeAttribute('data-ugc-revealing'), 380);
          });
        }
      } finally {
        this.setLoadMoreState(false);
      }
      this.emit('ulif:ugc_load_more', null, { revealed: revealedCards.length });
    }

    revealCard(card) {
      const parsedIndex = Number(card?.dataset.ugcIndex);
      if (!Number.isFinite(parsedIndex)) return;
      this.setCurrentVisibleLimit(Math.max(this.getCurrentVisibleLimit(), parsedIndex + 1));
      this.applyCardVisibility();
    }

    setLoadMoreState(isLoading) {
      this.isLoadingMore = isLoading;
      if (this.loadMoreButton) {
        this.loadMoreButton.disabled = isLoading;
        this.loadMoreButton.toggleAttribute('aria-busy', isLoading);
      }
      if (this.loadMoreLabel) this.loadMoreLabel.textContent = isLoading ? 'Loading' : 'Load more';
      if (this.loadMoreSpinner) this.loadMoreSpinner.hidden = !isLoading;
      if (this.loadMoreArrow) this.loadMoreArrow.hidden = isLoading;
      if (!isLoading) this.updateLoadMoreControl();
    }

    updateLoadMoreControl() {
      const wrapper = this.querySelector('[data-ugc-load-more-wrap]');
      if (!wrapper) return;
      const remaining = this.getCards().filter((card) => card.hidden).length;
      wrapper.hidden = remaining === 0;
      const nextCount = Math.min(remaining, this.getCurrentLoadStep());
      if (this.loadMoreButton) this.loadMoreButton.setAttribute('aria-label', `Load ${nextCount} more creator styles`);
    }

    isHistoryEnabled() { return this.dataset.historyEnabled === 'true'; }

    writeUrl(record, mode) {
      if (!this.isHistoryEnabled()) return;
      const url = new URL(global.location.href);
      const isShareable = !this.duplicateAssetIds.has(record.id);
      if (isShareable) url.searchParams.set(URL_PARAMETER, record.id);
      else url.searchParams.delete(URL_PARAMETER);
      const state = {
        ...(global.history.state || {}),
        [HISTORY_KEY]: {
          id: isShareable ? record.id : null,
          recordKey: record.recordKey,
          section: this.dataset.sectionId,
        },
      };
      if (mode === 'push') global.history.pushState(state, '', url);
      else global.history.replaceState(state, '', url);
    }

    clearUrl() {
      if (!this.isHistoryEnabled()) return;
      const marker = global.history.state?.[HISTORY_KEY];
      if (marker?.section === this.dataset.sectionId) {
        global.history.back();
        return;
      }
      const url = new URL(global.location.href);
      if (!url.searchParams.has(URL_PARAMETER)) return;
      url.searchParams.delete(URL_PARAMETER);
      global.history.replaceState(global.history.state, '', url);
    }

    syncFromUrl() {
      if (!this.isHistoryEnabled()) return;
      const id = new URL(global.location.href).searchParams.get(URL_PARAMETER);
      const index = id ? findUniqueCreatorIndexById(this.records, id) : -1;
      if (index >= 0) this.openByIndex(index, { historyMode: 'none', restoreFocus: false });
      else if (this.isDialogOpen()) this.closeDialog({ updateUrl: false, restoreFocus: false });
    }

    isDialogOpen() { return Boolean(this.dialog?.open || this.dialog?.hasAttribute('open')); }

    getProductTemplates(recordKey) {
      return [...this.querySelectorAll('template[data-ugc-product-template]')]
        .filter((template) => template.dataset.ugcProductTemplate === recordKey);
    }

    renderProduct(record, index = 0) {
      if (!this.productHost || !this.productRegion) return;
      const templates = this.getProductTemplates(record.recordKey);
      this.productHost.replaceChildren();
      if (!templates.length) {
        this.productRegion.hidden = true;
        this.activeProductIndex = -1;
        this.activeProductHandle = null;
        this.updateProductControls(0);
        return;
      }

      const safeIndex = Math.min(Math.max(0, index), templates.length - 1);
      const template = templates[safeIndex];
      this.activeProductIndex = safeIndex;
      this.activeProductHandle = template.dataset.ugcProductHandle || null;
      this.productRegion.hidden = false;
      this.productHost.append(template.content.cloneNode(true));
      this.updateProductControls(templates.length);
    }

    moveProduct(direction) {
      const record = this.getActiveRecord();
      if (!record) return;
      const templates = this.getProductTemplates(record.recordKey);
      if (templates.length <= 1) return;
      const nextIndex = nextProductIndex(this.activeProductIndex, templates.length, direction);
      this.renderProduct(record, nextIndex);
      this.emit('ulif:ugc_product_change', record, {
        productCount: templates.length,
        productHandle: this.activeProductHandle,
        productIndex: this.activeProductIndex,
      });
    }

    updateProductControls(productCount) {
      if (this.productControls) this.productControls.hidden = productCount <= 1;
      if (this.productCounter) {
        this.productCounter.textContent = productCount > 1
          ? `${this.activeProductIndex + 1} / ${productCount}`
          : '';
      }
    }

    clearProduct() {
      this.productHost?.replaceChildren();
      if (this.productRegion) this.productRegion.hidden = true;
      this.activeProductIndex = -1;
      this.activeProductHandle = null;
      this.updateProductControls(0);
    }

    clearVideo() {
      if (!this.dialogVideo) return;
      if (!this.dialogVideo.paused) this.dialogVideo.pause();
      this.dialogVideo.removeAttribute('src');
      this.dialogVideo.removeAttribute('poster');
      this.dialogVideo.load();
      this.dialogVideo.remove();
      this.dialogVideo = null;
      if (this.videoHost) this.videoHost.hidden = true;
    }

    createVideo(record) {
      if (!this.videoHost || !record.videoSrc) return;
      const video = global.document.createElement('video');
      video.controls = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.poster = record.posterSrc;
      video.src = record.videoSrc;
      video.addEventListener('error', () => {
        if (this.dialogVideo !== video) return;
        if (this.dialogImage) this.dialogImage.hidden = false;
        this.clearVideo();
      }, { once: true });
      this.dialogVideo = video;
      this.videoHost.hidden = false;
      this.videoHost.append(video);
      video.load();
    }

    emit(eventName, record, extraDetail = {}) {
      this.dispatchEvent(new CustomEvent(eventName, {
        bubbles: true,
        composed: true,
        detail: {
          sectionId: this.dataset.sectionId || null,
          ugcId: record?.id || null,
          creatorHandle: record?.creatorHandle || null,
          productHandle: record?.productHandle || null,
          ...extraDetail,
        },
      }));
    }
  }

  if (!global.customElements.get(ELEMENT_NAME)) global.customElements.define(ELEMENT_NAME, UlifCreatorStylesv4);
  if (!global[SECTION_LOAD_FLAG]) {
    global[SECTION_LOAD_FLAG] = true;
    global.document.addEventListener('shopify:section:load', (event) => {
      const target = event.target;
      if (!(target instanceof global.Element)) return;
      const sections = [];
      if (target.matches(ELEMENT_NAME)) sections.push(target);
      sections.push(...target.querySelectorAll(ELEMENT_NAME));
      sections.forEach((section) => section.reinitialize());
    });
  }
})(globalThis);
